/**
 * Backups on screen (decision 72; part 24e log, tables N, M, Y′, principle 9): the settings' Backups section (the
 * folder, each book's last backup, the plaintext warning), the notice and the reminder under the toolbar, never over the
 * text, and each book's last backup as words. What the notice and the reminder say is announced once (Y′3), by the
 * workspace's announcers. Part 41b: the backup password's row and notices beside them (table E).
 */
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import type { BackupNotice, Reminder } from '../backup/controller.ts';
import { focusOn } from './announce.ts';
import { LockCheckNotice, LockRow, plainText } from './backup-lock.ts';
import { shownBook } from './chapter-list.ts';
import { el, each, when } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import { Later, Section } from './setting-rows.ts';
import Button from '../ui/Button.ts';
import SettingsRow from '../ui/SettingsRow.ts';
import { component } from '../ui/solid.ts';
import { JobStatus } from './job-status.ts';
import type { Shell } from './shell.ts';

/** Principle 8: a book's last backup as words, from whole days since it (null: none). */
export function ageText(shell: Shell, days: number | null): string {
  const { t } = shell;
  if (days === null) return t('backup.never');
  return days === 0 ? t('backup.today') : t('backup.age', { count: days });
}

/** N6: the titles of books `ids`, as the book list shows them. */
function titles(shell: Shell, ids: readonly string[]): string {
  return ids
    .map((id) => {
      const book = shell.workspace.books().find((each) => each.id === id);
      return book === undefined ? shell.t('book.untitled') : shownBook(shell, book);
    })
    .join(', ');
}

/** Table N: what a notice says. */
export function noticeText(shell: Shell, notice: BackupNotice): string {
  const { t } = shell;
  switch (notice.kind) {
    case 'books':
      return t('backup.books', { count: notice.books.length, titles: titles(shell, notice.books) });
    case 'paused':
      return t('backup.paused');
    case 'refused':
      return t('backup.refused');
    case 'gone':
      return t('backup.gone');
    case 'full':
      return t('backup.full');
    case 'failed':
      return t('backup.failed');
    case 'lost':
      return t('backup.lost');
    case 'outdated':
      return t('backup.outdated');
  }
}

/** M6: what the reminder says: the book, and how many others wait. */
export function reminderText(shell: Shell): string {
  const reminder = shell.backups.reminder();
  if (reminder === null) return '';
  const { t } = shell;
  return [t('backup.reminder', { title: reminder.title }), ...(reminder.others > 0 ? [t('backup.reminderOthers', { count: reminder.others })] : [])].join(' ');
}

/** Part 41b: what the writer put away with Later of the lock's notices (the controller keeps no "later" for them). */
interface PutAway {
  /** E3: the books not backed up last put away (a run's next list is said again). */
  readonly unlocked: Accessor<readonly string[] | null>;
  readonly setUnlocked: (books: readonly string[]) => void;
  /** E7: the unreadable lock's notice, put away until the page is opened again (the settings' row still says it). */
  readonly paused: Accessor<boolean>;
  readonly setPaused: (away: boolean) => void;
}

const PUT_AWAY = new WeakMap<Shell, PutAway>();

/** Part 41b: this page's put-away lock notices. */
function putAway(shell: Shell): PutAway {
  let away = PUT_AWAY.get(shell);
  if (away === undefined) {
    const [unlocked, setUnlocked] = createSignal<readonly string[] | null>(null);
    const [paused, setPaused] = createSignal(false);
    away = { unlocked, setUnlocked, paused, setPaused };
    PUT_AWAY.set(shell, away);
  }
  return away;
}

/** Part 41b, E3: the books whose locked file couldn't be checked, so nothing was written for them; '' for none. */
export function unlockedText(shell: Shell): string {
  const books = shell.backups.unlocked();
  return books.length === 0 ? '' : shell.t('lock.unlocked', { count: books.length, titles: titles(shell, books) });
}

/** Part 41b, E3, E7, E15: what the backup password's notices under the toolbar say now ('' for each not shown). */
function lockNews(shell: Shell): string[] {
  const { t, backups } = shell;
  const away = putAway(shell);
  return [
    away.unlocked() === backups.unlocked() ? '' : unlockedText(shell),
    backups.lock() === 'unreadable' && !away.paused() ? t('lock.unreadable') : '',
    backups.lockCheckDue() ? t('lock.notice') : '',
  ];
}

/** Y′3: what the backups announce: the notice, then the reminder; part 41b: then the backup password's notices. */
export function backupNews(shell: Shell): string {
  const notice = shell.backups.notice();
  return [notice === null ? '' : noticeText(shell, notice), reminderText(shell), ...lockNews(shell)].filter((text) => text !== '').join(' ');
}

/** A notice's button: its way on, or (ghost) Later. */
function button(label: string, onClick: () => void, variant: 'secondary' | 'ghost' = 'secondary'): Child {
  return component(Button, { variant, size: 'sm', onClick, children: label });
}

/** N1–N7: the notice's one way on, and Later. */
function noticeButtons(shell: Shell, notice: BackupNotice): Child {
  const { t, backups } = shell;
  // A24: the region goes with the press, so the focus goes back to the text.
  const after = (work: () => Promise<void>) => () => {
    void work();
    shell.focusEditor();
  };
  const way = ((): Child => {
    switch (notice.kind) {
      case 'paused':
        return button(t('backup.allow'), after(() => backups.allow()));
      case 'refused':
      case 'gone':
      case 'lost':
        return button(t('backup.choose'), after(() => backups.choose()));
      case 'full':
      case 'failed':
      case 'books':
        return button(t('export.retry'), after(() => backups.backUpNow()));
      case 'outdated':
        return button(t('outdated.reload'), shell.reload);
    }
  })();
  return [way, button(t('backup.later'), after(() => backups.later()), 'ghost')];
}

/** M6, M7: the reminder's one press (share, else save) and Later. */
function reminderButtons(shell: Shell, reminder: Reminder): Child {
  const { t, backups } = shell;
  const done = (): void => {
    shell.focusEditor();
  };
  const save = backups.canShare()
    ? button(t('backup.share'), () => {
        void backups.shareReminder().then(done);
      })
    : component(Button, {
        size: 'sm',
        class: 'save-backup',
        href: reminder.url,
        download: reminder.name,
        onClick: () => {
          void backups.downloaded().then(done);
        },
        children: t('backup.download'),
      });
  return [
    save,
    // P3: a browser that can keep a folder offers it here too.
    backups.folder().kind === 'none' ? button(t('backup.choose'), () => void backups.choose().then(done)) : null,
    button(
      t('backup.later'),
      () => {
        void backups.later();
        done();
      },
      'ghost',
    ),
  ];
}

/** Y′3: the notice and the reminder, under the toolbar while either stands (PLAN.md §3, step 8: job-status strips). */
export function BackupStatus(shell: Shell): Child {
  const { t, backups } = shell;
  return [
    () => {
      const notice = backups.notice();
      return notice === null ? null : JobStatus({ class: 'backup-status', label: t('backup.label'), tone: () => 'warn', children: el('p', {}, noticeText(shell, notice)), actions: noticeButtons(shell, notice) });
    },
    () => {
      const reminder = backups.reminder();
      return reminder === null
        ? null
        : JobStatus({
            class: 'backup-reminder',
            label: t('backup.reminderLabel'),
            tone: () => 'info',
            children: [el('p', {}, reminderText(shell)), el('p', { class: 'hint' }, t(reminder.lock === null ? 'backup.plain' : 'backup.locked'))],
            actions: reminderButtons(shell, reminder),
          });
    },
    // Part 41b, E3: books not backed up because their locked file couldn't be checked: Try again, or Later.
    () => {
      const text = putAway(shell).unlocked() === backups.unlocked() ? '' : unlockedText(shell);
      return text === ''
        ? null
        : JobStatus({
            class: 'backup-unlocked',
            label: t('backup.label'),
            tone: () => 'warn',
            children: el('p', {}, text),
            actions: [
              button(t('export.retry'), () => {
                void backups.backUpNow();
                shell.focusEditor();
              }),
              button(
                t('backup.later'),
                () => {
                  putAway(shell).setUnlocked(backups.unlocked());
                  shell.focusEditor();
                },
                'ghost',
              ),
            ],
          });
    },
    // Part 41b, E7: the stored lock unreadable, so nothing is written: Set password, or Later.
    when(
      () => backups.lock() === 'unreadable' && !putAway(shell).paused(),
      () =>
        JobStatus({
          class: 'backup-lock-paused',
          label: t('lock.label'),
          tone: () => 'warn',
          children: el('p', {}, t('lock.unreadable')),
          actions: [
            button(t('lock.set'), () => {
              shell.ui.setDialog({ kind: 'backup-lock', task: 'set' });
            }),
            button(
              t('backup.later'),
              () => {
                putAway(shell).setPaused(true);
                shell.focusEditor();
              },
              'ghost',
            ),
          ],
        }),
    ),
    // Part 41b, E15–E17: the backup password's check, when due.
    LockCheckNotice(shell),
  ];
}

/**
 * Y′1 (part 11e, SP13, SP14): the settings' Backups and sync section: the folder and its buttons, each book's last backup,
 * the plaintext warning; and sync, which comes later.
 */
export function BackupSettings(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, backups, workspace } = shell;
  const folder = backups.folder;
  return Section(
    'settings-backups',
    t('settings.backupsSync'),
    heading,
    // PLAN.md §3, step 1: a SettingsRow; its help says where backups go and how.
    component(SettingsRow, {
      label: t('settings.backupFolder'),
      get help() {
        return [
          el('span', { class: 'backup-folder' }, () => {
            const now = folder();
            return now.kind === 'unsupported' ? t('backup.unsupported') : now.kind === 'none' ? t('backup.none') : t('backup.folder', { name: now.name });
          }),
          when(() => folder().kind !== 'unsupported', () => el('span', { class: 'backup-how' }, t('backup.how'))),
        ];
      },
      get children() {
        return when(
          () => folder().kind !== 'unsupported',
          () =>
            component(Button, {
              size: 'sm',
              class: 'choose-backup',
              onClick: () => void backups.choose(),
              get children() {
                return folder().kind === 'set' ? t('backup.change') : t('backup.choose');
              },
            }),
        );
      },
    }),
    // r1-1: what the notice under the toolbar says shows here too (the section's own buttons are its ways on).
    () => {
      const notice = backups.notice();
      return notice === null ? null : el('p', { class: 'backup-notice' }, noticeText(shell, notice));
    },
    // Part 41b, E3: so do the books a locked backup's check left out (E7 is the row's own).
    () => {
      const text = unlockedText(shell);
      return text === '' ? null : el('p', { class: 'backup-notice backup-unlocked-notice' }, text);
    },
    when(
      () => folder().kind === 'set',
      () =>
        el(
          'div',
          { class: 'field backup-actions' },
          component(Button, {
            size: 'sm',
            class: 'backup-now',
            get disabled() {
              return backups.running();
            },
            onClick: () => void backups.backUpNow(),
            get children() {
              return backups.running() ? t('backup.running') : t('backup.now');
            },
          }),
          component(Button, {
            variant: 'ghost',
            size: 'sm',
            onClick: () => {
              void backups.forget().then(() => {
                focusOn(shell, '.choose-backup');
              });
            },
            children: t('backup.forget'),
          }),
        ),
    ),
    el(
      'ul',
      { class: 'backup-ages' },
      each(workspace.books, (book) => el('li', {}, () => t('backup.bookAge', { title: shownBook(shell, book), age: ageText(shell, backups.age(book.id)) }))),
    ),
    // Part 41b, O23: the disclosure follows the backup password.
    el('p', { class: 'hint backup-plain' }, () => plainText(shell)),
    LockRow(shell),
    Later(shell, t('settings.syncOff'), t('settings.syncHelp'), t('settings.syncOn')),
  );
}
